import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag, Typography, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import client from '../../api/client';

const ACCOUNT_TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];

const AccountMappings = () => {
    const [accounts, setAccounts] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [assigningId, setAssigningId] = useState(null);
    const [editing, setEditing] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form] = Form.useForm();

    const fetchData = async () => {
        setLoading(true);
        try {
            const [accountsResponse, mappingsResponse] = await Promise.all([
                client.get('/master/coa'),
                client.get('/master/account-mappings'),
            ]);
            setAccounts(accountsResponse.data || []);
            setMappings(mappingsResponse.data || []);
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to load account mappings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const accountOptionsByType = useMemo(() => {
        return ACCOUNT_TYPES.reduce((map, type) => {
            map[type] = accounts
                .filter((account) => account.is_active && account.type === type)
                .map((account) => ({
                    label: `${account.code} - ${account.name}`,
                    value: account.id,
                }));
            return map;
        }, {});
    }, [accounts]);

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({
            allowed_account_types: [],
            is_required: true,
            is_active: true,
            sort_order: (mappings.length + 1) * 10,
        });
        setIsModalOpen(true);
    };

    const openEdit = (mapping) => {
        setEditing(mapping);
        form.setFieldsValue({
            key: mapping.key,
            label: mapping.label,
            description: mapping.description,
            allowed_account_types: mapping.allowed_account_types || [],
            is_required: mapping.is_required,
            is_active: mapping.is_active,
            sort_order: mapping.sort_order,
            chart_of_account_id: mapping.chart_of_account_id,
        });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditing(null);
        form.resetFields();
    };

    const saveMapping = async (values) => {
        setSaving(true);
        try {
            const payload = {
                ...values,
                key: editing?.is_system ? editing.key : values.key,
                allowed_account_types: values.allowed_account_types || [],
                chart_of_account_id: values.chart_of_account_id || null,
            };

            if (editing) {
                await client.put(`/master/account-mappings/${editing.id}`, payload);
                message.success('Mapping updated');
            } else {
                await client.post('/master/account-mappings', payload);
                message.success('Mapping created');
            }

            closeModal();
            fetchData();
        } catch (error) {
            const validation = error.response?.data?.errors
                ? Object.values(error.response.data.errors).flat().join(' ')
                : null;
            message.error(error.response?.data?.message || validation || 'Failed to save mapping');
        } finally {
            setSaving(false);
        }
    };

    const assignMapping = async (mapping, accountId) => {
        setAssigningId(mapping.id);
        try {
            await client.post(`/master/account-mappings/${mapping.id}/assign`, {
                chart_of_account_id: accountId || null,
            });
            message.success('Mapping assignment updated');
            fetchData();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to assign account');
        } finally {
            setAssigningId(null);
        }
    };

    const deleteMapping = async (mapping) => {
        try {
            await client.delete(`/master/account-mappings/${mapping.id}`);
            message.success('Mapping deleted');
            fetchData();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to delete mapping');
        }
    };

    const columns = [
        {
            title: 'Mapping',
            dataIndex: 'label',
            key: 'label',
            render: (_, record) => (
                <Space direction="vertical" size={2}>
                    <Space wrap>
                        <Typography.Text strong>{record.label}</Typography.Text>
                        {record.is_system && <Tag color="blue">System</Tag>}
                        {record.is_required && !record.chart_of_account_id && record.is_active && <Tag color="red">Missing</Tag>}
                        {!record.is_active && <Tag>Inactive</Tag>}
                    </Space>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{record.key}</Typography.Text>
                    {record.description && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>{record.description}</Typography.Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'Allowed Types',
            dataIndex: 'allowed_account_types',
            key: 'allowed_account_types',
            width: 180,
            render: (types) => (types?.length ? types.map((type) => <Tag key={type}>{type}</Tag>) : <Tag>Any active account</Tag>),
        },
        {
            title: 'Mapped Account',
            key: 'chart_of_account_id',
            width: 380,
            render: (_, record) => {
                const allowedTypes = record.allowed_account_types || [];
                const options = allowedTypes.length
                    ? allowedTypes.flatMap((type) => accountOptionsByType[type] || [])
                    : accounts
                        .filter((account) => account.is_active)
                        .map((account) => ({ label: `${account.code} - ${account.name}`, value: account.id }));

                return (
                    <Select
                        allowClear
                        showSearch
                        placeholder="Select account"
                        optionFilterProp="label"
                        options={options}
                        value={record.chart_of_account_id || null}
                        onChange={(value) => assignMapping(record, value)}
                        loading={assigningId === record.id}
                        style={{ width: '100%' }}
                    />
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    {!record.is_system && (
                        <Popconfirm
                            title="Delete mapping?"
                            description="This removes the mapping slot, not the chart account."
                            onConfirm={() => deleteMapping(record)}
                            okText="Delete"
                            cancelText="Cancel"
                        >
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <Card
            className="premium-card"
            title="Account Mappings"
            extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Mapping</Button>}
        >
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Map system transactions to your chart of accounts. Required mappings must be assigned before related transactions can post.
            </Typography.Text>
            <Table
                rowKey="id"
                columns={columns}
                dataSource={mappings}
                loading={loading}
                pagination={false}
                scroll={{ x: 900 }}
            />

            <Modal
                title={editing ? 'Edit Account Mapping' : 'Add Account Mapping'}
                open={isModalOpen}
                onCancel={closeModal}
                footer={null}
                destroyOnHidden
            >
                <Form form={form} layout="vertical" onFinish={saveMapping}>
                    <Form.Item
                        name="key"
                        label="Mapping Key"
                        rules={[
                            { required: true, message: 'Mapping key is required' },
                            { pattern: /^[a-z][a-z0-9_]*$/, message: 'Use lowercase letters, numbers, and underscores only' },
                        ]}
                    >
                        <Input placeholder="example_mapping_key" disabled={editing?.is_system} />
                    </Form.Item>
                    <Form.Item name="label" label="Label" rules={[{ required: true, message: 'Label is required' }]}>
                        <Input placeholder="Mapping label" />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name="allowed_account_types" label="Allowed Account Types">
                        <Select mode="multiple" allowClear options={ACCOUNT_TYPES.map((type) => ({ label: type, value: type }))} />
                    </Form.Item>
                    <Form.Item name="chart_of_account_id" label="Mapped Account">
                        <Select
                            allowClear
                            showSearch
                            placeholder="Select account"
                            optionFilterProp="label"
                            options={accounts
                                .filter((account) => account.is_active)
                                .map((account) => ({
                                    label: `${account.code} - ${account.name}`,
                                    value: account.id,
                                }))}
                        />
                    </Form.Item>
                    <Space style={{ width: '100%' }} size="middle" align="start">
                        <Form.Item name="is_required" label="Required" style={{ flex: 1 }}>
                            <Select options={[
                                { label: 'Required', value: true },
                                { label: 'Optional', value: false },
                            ]} />
                        </Form.Item>
                        <Form.Item name="is_active" label="Status" style={{ flex: 1 }}>
                            <Select options={[
                                { label: 'Active', value: true },
                                { label: 'Inactive', value: false },
                            ]} />
                        </Form.Item>
                        <Form.Item name="sort_order" label="Sort Order" style={{ flex: 1 }}>
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </Space>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <Button onClick={closeModal}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={saving}>
                            {editing ? 'Save Changes' : 'Create Mapping'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </Card>
    );
};

export default AccountMappings;
